import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  ViewChild,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import Highcharts from "highcharts";
import * as topojson from "topojson-client";
import { environment } from "../../../environments/environment";
import qcaManagementData from "../../../json/qca-management.mock.json";
import pssManagementData from "../../../json/pss-management.mock.json";
import generatorManagementData from "../../../json/generators-management.mock.json";

type ChartMode = "time" | "block";
type ChartKey = "trend" | "blocks" | "generation" | "sourceShare";
type EntityView = "QCA" | "PSS" | "GENERATOR";
type EntityDeviation = {
  id: string;
  name: string;
  type: EntityView;
  overInjection: number;
  underInjection: number;
  deviation: number;
  deviationPercent: number;
  dsmAmount: number;
  qcaCode?: string;
  pssCode?: string;
};
type DistrictDeviation = {
  district: string;
  deviation_percentage: number;
};
interface DashboardAlert {
  severity: "critical" | "warning" | "success";
  icon: string;
  title: string;
  detail: string;
  plant: string;
  block: number;
  value: string;
  status: string;
  time: string;
}

type DistrictGeoJsonFeature = {
  properties?: { district?: string; [key: string]: unknown };
};

@Component({
  selector: "app-dsm-dashboard",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./dsm-dashboard.component.html",
  styleUrl: "./dsm-dashboard.component.scss",
})
export class DsmDashboardComponent implements AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  @ViewChild("trendChart") private trendChartElement?: ElementRef<HTMLElement>;
  @ViewChild("blockChart") private blockChartElement?: ElementRef<HTMLElement>;
  @ViewChild("trendPanel") private trendPanelElement?: ElementRef<HTMLElement>;
  @ViewChild("blockPanel") private blockPanelElement?: ElementRef<HTMLElement>;
  @ViewChild("generationChart")
  private generationChartElement?: ElementRef<HTMLElement>;
  @ViewChild("sourceShareChart")
  private sourceShareChartElement?: ElementRef<HTMLElement>;
  @ViewChild("districtMap") private districtMapElement?: ElementRef<HTMLElement>;
  @ViewChild("districtMapLegend")
  private districtMapLegendElement?: ElementRef<HTMLElement>;

  readonly selectedDate = signal(this.today());
  readonly currentBlock = signal(this.blockLabelFor(new Date()));
  readonly isLive = signal(true);
  readonly autoRefresh = signal(true);
  readonly entityView = signal<EntityView>("QCA");
  readonly modes: Record<"trend" | "blocks", ChartMode> = {
    trend: "time",
    blocks: "block",
  };
  readonly sourceShare = [
    { name: "Solar", value: "42.1%", color: "#ffb21a" },
    { name: "Wind", value: "28.6%", color: "#1eaf69" },
  ];
  readonly blockDeviations = this.createBlockDeviations();
  readonly availability = [
    ["PSS Data Availability", "98.62%"],
    ["Generator Data Availability", "97.45%"],
    ["Real-time Generation Data", "Live"],
    ["Weather Data", "Live"],
    ["Meter Data", "Live"],
  ];

  entityRows(): EntityDeviation[] {
    return this.blockDeviations
      .filter((row) => row.type === this.entityView())
      .slice(0, 10);
  }

  private createBlockDeviations(): EntityDeviation[] {
    const qcas = (qcaManagementData as any).rows ?? [];
    const psses = (pssManagementData as any).rows ?? [];
    const generators = (generatorManagementData as any).rows ?? [];
    const buildRow = (
      id: string,
      name: string,
      type: EntityView,
      capacity: number,
      index: number,
      qcaCode?: string,
      pssCode?: string,
    ): EntityDeviation => {
      const overInjection = Number((capacity * (0.012 + (index % 3) * 0.004)).toFixed(2));
      const underInjection = Number((capacity * (0.007 + (index % 4) * 0.002)).toFixed(2));
      const deviation = Number((overInjection + underInjection).toFixed(2));
      return {
        id,
        name,
        type,
        overInjection,
        underInjection,
        deviation,
        deviationPercent: Number(((deviation / Math.max(capacity, 1)) * 100).toFixed(2)),
        dsmAmount: Math.round(deviation * 6200),
        qcaCode,
        pssCode,
      };
    };

    return [
      ...qcas.map((qca: any, index: number) =>
        buildRow(qca.code, qca.name, "QCA", qca.totalCapacityMw ?? 0, index, qca.code),
      ),
      ...psses.map((pss: any, index: number) =>
        buildRow(pss.code, pss.name, "PSS", pss.installedCapacityMw ?? 0, index, pss.qcaCode, pss.code),
      ),
      ...generators.map((generator: any, index: number) =>
        buildRow(
          generator.code,
          generator.companyName ?? generator.name,
          "GENERATOR",
          generator.generatorCapacityMw ?? generator.installedCapacityMw ?? 0,
          index,
          generator.qcaCode,
          generator.pssCode,
        ),
      ),
    ].sort((left, right) => right.deviation - left.deviation);
  }

  navigateToEntity(row: EntityDeviation): void {
    if (row.type === "QCA" && row.qcaCode) {
      void this.router.navigate(["/qca-dashboard"], {
        queryParams: { qca: row.qcaCode },
      });
      return;
    }
    if (row.type === "PSS" && row.pssCode) {
      void this.router.navigate(["/pss-dashboard", row.pssCode]);
      return;
    }
    void this.router.navigate(["/view-schedule"], {
      queryParams: { generator: row.name },
    });
  }
readonly alerts: readonly DashboardAlert[] = [
  {
    severity: "critical",
    icon: "!",
    title: "High Deviation Alert",
    detail: "Schedule vs Actual deviation exceeded permitted limit",
    plant: "SolarGen Pvt Ltd",
    block: 24,
    value: "-12.4 MW",
    status: "Critical",
    time: "10:45 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Data Delay Alert",
    detail: "Meter data has not been received",
    plant: "WindPower Ltd",
    block: 24,
    value: "08 min",
    status: "Warning",
    time: "10:30 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Schedule Updated",
    detail: "Revision schedule successfully updated",
    plant: "Future Power Ltd",
    block: 25,
    value: "+8.6 MW",
    status: "Resolved",
    time: "10:15 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "Generation Deviation",
    detail: "Actual generation exceeded scheduled generation",
    plant: "PSS Nagpur",
    block: 24,
    value: "+15.2 MW",
    status: "Critical",
    time: "10:00 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Meter Data Delay",
    detail: "Meter feed response delayed beyond expected interval",
    plant: "SolarGen Pvt Ltd",
    block: 23,
    value: "05 min",
    status: "Warning",
    time: "09:45 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Schedule Updated",
    detail: "Day-ahead schedule successfully updated",
    plant: "WindPower Ltd",
    block: 23,
    value: "124.5 MW",
    status: "Resolved",
    time: "09:30 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Generator Outage",
    detail: "Generation capacity is temporarily unavailable",
    plant: "PSS-102",
    block: 23,
    value: "48 MW",
    status: "Warning",
    time: "09:15 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Intraday Revision Submitted",
    detail: "Intraday revision was submitted successfully",
    plant: "PSS-205",
    block: 22,
    value: "Rev-2",
    status: "Resolved",
    time: "09:00 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "SCADA Data Delayed",
    detail: "Telemetry update delayed for more than 5 minutes",
    plant: "PSS-101",
    block: 22,
    value: "05 min",
    status: "Critical",
    time: "08:45 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "SCADA Connection Restored",
    detail: "Plant telemetry is reporting normally",
    plant: "PSS-008",
    block: 21,
    value: "Online",
    status: "Resolved",
    time: "08:30 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "Under Injection Alert",
    detail: "Actual generation is significantly below schedule",
    plant: "GreenSolar Energy",
    block: 20,
    value: "-18.7 MW",
    status: "Critical",
    time: "08:15 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Communication Delay",
    detail: "Communication response from plant is delayed",
    plant: "MahaWind Energy",
    block: 20,
    value: "06 min",
    status: "Warning",
    time: "08:00 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Meter Data Received",
    detail: "Latest meter data received successfully",
    plant: "PSS-115",
    block: 19,
    value: "96.8 MW",
    status: "Resolved",
    time: "07:45 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "Over Injection Alert",
    detail: "Generation is above the scheduled limit",
    plant: "SunPower Maharashtra",
    block: 19,
    value: "+21.3 MW",
    status: "Critical",
    time: "07:30 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Forecast Mismatch",
    detail: "Actual generation differs from forecast value",
    plant: "SolarGen Pvt Ltd",
    block: 18,
    value: "11.5%",
    status: "Warning",
    time: "07:15 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Revision Approved",
    detail: "Schedule revision has been approved successfully",
    plant: "Future Power Ltd",
    block: 18,
    value: "Rev-3",
    status: "Resolved",
    time: "07:00 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "Plant Offline",
    detail: "Plant communication and telemetry are unavailable",
    plant: "PSS-302",
    block: 17,
    value: "Offline",
    status: "Critical",
    time: "06:45 AM",
  },
  {
    severity: "warning",
    icon: "!",
    title: "Low Generation Alert",
    detail: "Generation dropped below the expected threshold",
    plant: "WindPower Ltd",
    block: 17,
    value: "42.6 MW",
    status: "Warning",
    time: "06:30 AM",
  },
  {
    severity: "success",
    icon: "✓",
    title: "Schedule Synchronised",
    detail: "Schedule successfully synchronized with plant data",
    plant: "PSS-210",
    block: 16,
    value: "Synced",
    status: "Resolved",
    time: "06:15 AM",
  },
  {
    severity: "critical",
    icon: "!",
    title: "Deviation Limit Breached",
    detail: "Deviation crossed the configured DSM threshold",
    plant: "Maharashtra Solar Park",
    block: 16,
    value: "-14.8 MW",
    status: "Critical",
    time: "06:00 AM",
  },
];
  /** DSM data is matched to the Maharashtra district GeoJSON by district name. */
  readonly districtDeviations: DistrictDeviation[] = [
    { district: "Mumbai City", deviation_percentage: 4.8 },
    { district: "Pune", deviation_percentage: -3.2 },
    { district: "Nagpur", deviation_percentage: 2.6 },
    { district: "Nashik", deviation_percentage: -5.1 },
    { district: "Thane", deviation_percentage: 1.9 },
    { district: "Chhatrapati Sambhajinagar", deviation_percentage: -2.7 },
    { district: "Kolhapur", deviation_percentage: 3.4 },
    { district: "Solapur", deviation_percentage: -4.3 },
    { district: "Amravati", deviation_percentage: 2.1 },
    { district: "Satara", deviation_percentage: -1.8 },
    { district: "Sangli", deviation_percentage: 3.0 },
    { district: "Ahmednagar", deviation_percentage: -2.4 },
    { district: "Jalgaon", deviation_percentage: 4.1 },
    { district: "Nanded", deviation_percentage: -3.6 },
    { district: "Akola", deviation_percentage: 1.5 },
  ];
  googleMapsError = false;
  get currentBlockNumber(): number {
    const [startTime] = this.currentBlock().split(" - ");
    const [hours, minutes] = startTime.split(":").map(Number);
    return Math.min(96, hours * 4 + Math.floor(minutes / 15) + 1);
  }
  private trendChart?: Highcharts.Chart;
  private blockChart?: Highcharts.Chart;
  private generationChart?: Highcharts.Chart;
  private sourceShareChart?: Highcharts.Chart;
  private googleMap?: any;
  private googleMapsApi?: any;
  private districtTooltip?: HTMLDivElement;
  private maharashtraBounds?: any;
  private mapResizeObserver?: ResizeObserver;
  private themeObserver?: MutationObserver;
  private mapControlObserver?: MutationObserver;
  private blockTimer?: ReturnType<typeof setTimeout>;
  private fullscreenFitTimer?: ReturnType<typeof setTimeout>;
  private readonly fullscreenChanged = () => {
    if (this.fullscreenFitTimer) clearTimeout(this.fullscreenFitTimer);
    this.fullscreenFitTimer = setTimeout(
      () => {
        this.resizeChartsForFullscreen();
        this.renderSourceShareChart();
      },
      300,
    );
  };

  ngAfterViewInit(): void {
    this.updateCurrentBlock();
    this.renderCharts();
    void this.initializeDistrictMap();
    this.scheduleNextBlockUpdate();
    document.addEventListener("fullscreenchange", this.fullscreenChanged);
  }
  ngOnDestroy(): void {
    if (this.blockTimer) clearTimeout(this.blockTimer);
    if (this.fullscreenFitTimer) clearTimeout(this.fullscreenFitTimer);
    document.removeEventListener("fullscreenchange", this.fullscreenChanged);
    this.trendChart?.destroy();
    this.blockChart?.destroy();
    this.generationChart?.destroy();
    this.sourceShareChart?.destroy();
    this.mapResizeObserver?.disconnect();
    this.themeObserver?.disconnect();
    this.mapControlObserver?.disconnect();
  }
  changeMode(key: "trend" | "blocks", value: string): void {
    this.modes[key] = value === "block" ? "block" : "time";
    this.renderCharts();
  }
  toggleFullscreen(panel: HTMLElement): void {
    document.fullscreenElement === panel
      ? void document.exitFullscreen()
      : void panel.requestFullscreen?.();
  }
  resetZoom(key: ChartKey): void {
    const charts: Record<ChartKey, Highcharts.Chart | undefined> = {
      trend: this.trendChart,
      blocks: this.blockChart,
      generation: this.generationChart,
      sourceShare: this.sourceShareChart,
    };
    charts[key]?.zoomOut();
  }
  toggleLive(): void {
    this.isLive.update((value) => !value);
  }
  toggleAutoRefresh(): void {
    this.autoRefresh.update((value) => !value);
    this.updateCurrentBlock();
    if (this.autoRefresh()) this.scheduleNextBlockUpdate();
  }
  updateSelectedDate(value: string): void {
    this.selectedDate.set(value);
    this.updateCurrentBlock(true);
  }

  private renderCharts(): void {
    const [blockStart] = this.currentBlock().split(" - ");
    const [hours, minutes] = blockStart.split(":").map(Number);
    const completedBlocks = Math.max(0, hours * 4 + Math.floor(minutes / 15));
    const categories = Array.from({ length: 96 }, (_, index) =>
      String(index + 1),
    );
    const timeLabels = (index: number) =>
      `${String(Math.floor(index / 4)).padStart(2, "0")}:00`;
    const xAxis = (mode: ChartMode): Highcharts.XAxisOptions => ({
      categories,
      tickPositions:
        mode === "time"
          ? Array.from({ length: 24 }, (_, i) => i * 4)
          : Array.from({ length: 96 }, (_, i) => i),
      labels: {
        rotation: mode === "time" ? -45 : 1,
        style: { fontSize: "10px"},
        formatter: function () {
          return mode === "time"
            ? timeLabels(this.pos as number)
            : String((this.pos as number) + 1);
        },
      },
      lineColor: "#d9e2ef",
    });
    const over = Array.from({ length: 96 }, (_, i) =>
      Math.round(
        450 +
          260 * Math.sin(i / 9) +
          170 * Math.sin(i / 3.2) +
          (i > 60 ? 130 : 0),
      ),
    );
    const under = over.map(
      (value, i) => -Math.round(value * (0.64 + 0.2 * Math.cos(i / 8))),
    );
    const net = over.map((value, i) => value + under[i]);
    const trendOver = this.visibleThroughPreviousBlock(over, completedBlocks);
    const trendUnder = this.visibleThroughPreviousBlock(under, completedBlocks);
    const trendNet = this.visibleThroughPreviousBlock(net, completedBlocks);
    const currentOver = this.visibleThroughPreviousBlock(over, completedBlocks);
    const currentUnder = this.visibleThroughPreviousBlock(under, completedBlocks);
    const currentNet = this.visibleThroughPreviousBlock(net, completedBlocks);
    this.trendChart?.destroy();
    this.blockChart?.destroy();
    if (this.trendChartElement)
      this.trendChart = Highcharts.chart(
        this.trendChartElement.nativeElement,
        this.deviationChartOptions(
          "Deviation Trend (MW)",
          xAxis(this.modes.trend),
          trendOver,
          trendUnder,
          trendNet,
          this.modes.trend,
        ),
      );
    if (this.blockChartElement)
      this.blockChart = Highcharts.chart(
        this.blockChartElement.nativeElement,
        this.deviationChartOptions(
          "Deviation by Time Block (MW)",
          xAxis(this.modes.blocks),
          currentOver,
          currentUnder,
          currentNet,
          this.modes.blocks,
        ),
      );
    this.renderGenerationChart();
    this.renderSourceShareChart();
  }
  /** Preserves all 96 x-axis labels but hides the active and future blocks. */
  private visibleThroughPreviousBlock(
    values: number[],
    completedBlocks: number,
  ): Array<number | null> {
    return values.map((value, index) =>
      index < completedBlocks ? value : null,
    );
  }
  private updateCurrentBlock(force = false): void {
    if (!force && !this.autoRefresh()) return;

    const now = new Date();
    const selectedDate = this.selectedDate();
    const today = this.today();
    if (selectedDate < today) {
      this.currentBlock.set("24:00 - 24:00");
    } else if (selectedDate > today) {
      this.currentBlock.set("00:00 - 00:15");
    } else {
      this.currentBlock.set(this.blockLabelFor(now));
    }
    if (this.trendChartElement) this.renderCharts();
  }
  /** Refresh exactly when a new 15-minute DSM block starts. */
  private scheduleNextBlockUpdate(): void {
    if (this.blockTimer) clearTimeout(this.blockTimer);
    if (!this.autoRefresh()) return;

    const now = new Date();
    const next = new Date(now);
    next.setMinutes((Math.floor(now.getMinutes() / 15) + 1) * 15, 0, 0);
    this.blockTimer = setTimeout(() => {
      this.updateCurrentBlock();
      this.scheduleNextBlockUpdate();
    }, Math.max(1_000, next.getTime() - now.getTime()));
  }
  private blockLabelFor(date: Date): string {
    const startMinutes = date.getHours() * 60 + Math.floor(date.getMinutes() / 15) * 15;
    const endMinutes = Math.min(1440, startMinutes + 15);
    return `${this.formatTime(startMinutes)} - ${this.formatTime(endMinutes)}`;
  }
  private formatTime(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }
  private today(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }
  private resizeChartsForFullscreen(): void {
    const chartSizes: Array<[Highcharts.Chart | undefined, number]> = [
      [this.trendChart, 250],
      [this.blockChart, 250],
      [this.generationChart, 210],
      [this.sourceShareChart, 310],
    ];
    chartSizes.forEach(([chart, normalHeight]) => {
      if (!chart) return;
      const panel = chart.container.closest<HTMLElement>(".chart-panel");
      const fullscreenHeight =
        panel && panel === document.fullscreenElement
          ? Math.max(320, panel.clientHeight - 36)
          : normalHeight;
      chart.setSize(null, fullscreenHeight, false);
      chart.reflow();
    });
    this.resizeAndFitDistrictMap();
  }
  private async initializeDistrictMap(): Promise<void> {
    const apiKey = (environment as typeof environment & { googleMapsKey?: string })
      .googleMapsKey;
    if (!apiKey || !this.districtMapElement || typeof document === "undefined") {
      this.googleMapsError = true;
      return;
    }

    try {
      await this.loadGoogleMaps(apiKey);
      const googleMaps = (window as unknown as { google: { maps: any } }).google.maps;
      this.googleMapsApi = googleMaps;
      this.googleMap = new googleMaps.Map(this.districtMapElement.nativeElement, {
        center: { lat: 19.7515, lng: 75.7139 },
        zoom: 6,
        minZoom: 6,
        mapTypeControl: true,
        mapTypeControlOptions: {
          position: googleMaps.ControlPosition.TOP_RIGHT,
          style: googleMaps.MapTypeControlStyle.HORIZONTAL_BAR,
          mapTypeIds: [googleMaps.MapTypeId.ROADMAP, googleMaps.MapTypeId.SATELLITE],
        },
        streetViewControl: false,
        fullscreenControl: true,
        fullscreenControlOptions: { position: googleMaps.ControlPosition.RIGHT_TOP },
        zoomControl: true,
        zoomControlOptions: { position: googleMaps.ControlPosition.LEFT_TOP },
        backgroundColor: document.documentElement.dataset["theme"] === "dark" ? "#303030" : "#ffffff",
        styles: this.googleMapTheme(),
      });
      if (this.districtMapLegendElement) {
        this.districtMapLegendElement.nativeElement.classList.add("map-control");
        this.googleMap.controls[googleMaps.ControlPosition.RIGHT_BOTTOM].push(
          this.districtMapLegendElement.nativeElement,
        );
      }
      await this.addDistrictDeviationLayer(googleMaps);
      await this.restrictMapToMaharashtra(googleMaps);
      this.mapResizeObserver = new ResizeObserver(() => this.resizeAndFitDistrictMap());
      this.mapResizeObserver.observe(this.districtMapElement.nativeElement);
      this.themeObserver = new MutationObserver(() => {
        this.applyGoogleMapTheme();
        this.renderCharts();
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
      this.suppressGoogleControlTooltips();
      this.mapControlObserver = new MutationObserver(() => this.suppressGoogleControlTooltips());
      this.mapControlObserver.observe(this.districtMapElement.nativeElement, {
        childList: true,
        subtree: true,
      });
      this.applyGoogleMapTheme();
    } catch (error) {
      console.error("DSM district map initialization failed:", error);
      this.googleMapsError = true;
    }
  }
  /** Adds Maharashtra district boundaries and applies the current DSM deviation to each polygon. */
  private async addDistrictDeviationLayer(googleMaps: any): Promise<void> {
    const deviations = new Map(
      this.districtDeviations.map((item) => [
        this.normaliseDistrictName(item.district),
        item,
      ]),
    );
    const response = await fetch("assets/geojson/maharashtra_district.json");
    if (!response.ok) throw new Error("Maharashtra district GeoJSON could not be loaded");
    const geoJson = (await response.json()) as { features?: DistrictGeoJsonFeature[] };

    (geoJson.features ?? []).forEach((feature) => {
      const district = feature.properties?.district ?? "Unknown district";
      const deviation = deviations.get(this.normaliseDistrictName(district));
      feature.properties = {
        ...feature.properties,
        displayDistrict: district,
        deviation: deviation?.deviation_percentage ?? null,
      };
    });
    this.googleMap.data.addGeoJson(geoJson);
    this.googleMap.data.setStyle((feature: any) => {
      const deviation = feature.getProperty("deviation") as number | null;
      const hasDeviation = typeof deviation === "number";
      return {
        fillColor: hasDeviation ? this.deviationColor(deviation) : "#cbd5e1",
        fillOpacity: hasDeviation ? 0.68 : 0.32,
        strokeColor: "#ffffff",
        strokeOpacity: 0.95,
        strokeWeight: 1.1,
        clickable: true,
      };
    });
    // Keep hover details below the hovered district, away from the area above the map.
    this.googleMap.data.addListener("mouseover", (event: any) => {
      this.googleMap.data.overrideStyle(event.feature, { strokeColor: "#0f172a", strokeWeight: 2 });
      this.showDistrictTooltip(event);
    });
    this.googleMap.data.addListener("mouseout", (event: any) => {
      this.googleMap.data.revertStyle(event.feature);
      this.hideDistrictTooltip();
    });

    const bounds = new googleMaps.LatLngBounds();
    this.googleMap.data.forEach((feature: any) =>
      feature.getGeometry().forEachLatLng((latLng: any) => bounds.extend(latLng)),
    );
    this.maharashtraBounds = bounds;
    this.fitDistrictBounds();
  }
  /** Masks the surrounding basemap so this DSM view displays Maharashtra only. */
  private async restrictMapToMaharashtra(googleMaps: any): Promise<void> {
    const response = await fetch("assets/geojson/maharashtra.json");
    if (!response.ok) throw new Error("Maharashtra boundary GeoJSON could not be loaded");

    const topology = await response.json();
    const stateObject = topology.objects?.["maharashtra-divisions"];
    if (!stateObject) throw new Error("Maharashtra boundary object could not be found");

    const state = topojson.merge(topology, stateObject.geometries) as {
      type: "Polygon" | "MultiPolygon";
      coordinates: number[][][] | number[][][][];
    };
    const polygons = state.type === "Polygon"
      ? [state.coordinates as number[][][]]
      : state.coordinates as number[][][][];
    const outerRings = polygons.map((polygon) => {
      const ring = polygon[0];
      const signedArea = ring.reduce((area, point, index) => {
        const next = ring[(index + 1) % ring.length];
        return area + point[0] * next[1] - next[0] * point[1];
      }, 0);
      return signedArea > 0 ? [...ring].reverse() : ring;
    });
    const bounds = new googleMaps.LatLngBounds();
    outerRings.forEach((ring) => ring.forEach(([lng, lat]) => bounds.extend({ lat, lng })));
    this.maharashtraBounds = bounds;
    this.googleMap.setOptions({
      restriction: { latLngBounds: bounds, strictBounds: false },
      minZoom: 6,
      maxZoom: 15,
    });

    this.googleMap.data.addGeoJson({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: { isOutsideMaharashtra: true },
          geometry: {
            type: "Polygon",
            coordinates: [
              [[55, 5], [100, 5], [100, 38], [55, 38], [55, 5]],
              ...outerRings,
            ],
          },
        },
      ],
    });
    this.applyDistrictMapDataStyle();
    this.fitDistrictBounds();
  }
  private fitDistrictBounds(): void {
    if (this.googleMap && this.maharashtraBounds && !this.maharashtraBounds.isEmpty()) {
      this.googleMap.fitBounds(this.maharashtraBounds, 12);
      if (document.fullscreenElement) {
        this.googleMapsApi.event.addListenerOnce(this.googleMap, "idle", () =>
          this.googleMap.setZoom(this.googleMap.getZoom() + 0.8),
        );
      }
    }
  }
  /** Wait for the fullscreen layout, then resize and refit the Google map. */
  private resizeAndFitDistrictMap(): void {
    if (!this.googleMap || !this.googleMapsApi || !this.maharashtraBounds) return;

    requestAnimationFrame(() => {
      this.googleMapsApi.event.trigger(this.googleMap, "resize");
      requestAnimationFrame(() => this.fitDistrictBounds());
    });
  }
  private applyGoogleMapTheme(): void {
    if (!this.googleMap) return;
    const dark = document.documentElement.dataset["theme"] === "dark";
    this.googleMap.setOptions({
      backgroundColor: dark ? "#303030" : "#ffffff",
      styles: this.googleMapTheme(),
    });
    this.applyDistrictMapDataStyle();
  }
  /** Google control titles render as browser-white tooltips, even on a dark map. */
  private suppressGoogleControlTooltips(): void {
    if (!this.districtMapElement) return;
    this.districtMapElement.nativeElement
      .querySelectorAll<HTMLElement>(".gm-style button[title], .gm-style [role='button'][title]")
      .forEach((control) => control.removeAttribute("title"));
  }
  /** Keep district colours readable while matching the Google basemap to the app theme. */
  private applyDistrictMapDataStyle(): void {
    if (!this.googleMap) return;
    const dark = document.documentElement.dataset["theme"] === "dark";
    this.googleMap.data.setStyle((feature: any) => {
      if (feature.getProperty("isOutsideMaharashtra")) {
        return {
          fillColor: dark ? "#17243a" : "#e5e7eb",
          fillOpacity: 1,
          strokeOpacity: 0,
          clickable: false,
        };
      }
      const deviation = feature.getProperty("deviation") as number | null;
      const hasDeviation = typeof deviation === "number";
      return {
        fillColor: hasDeviation ? this.deviationColor(deviation) : dark ? "#17243a" : "#cbd5e1",
        fillOpacity: hasDeviation ? 0.68 : 0.32,
        strokeColor: "#ffffff",
        strokeOpacity: 0.95,
        strokeWeight: 1.1,
        clickable: true,
      };
    });
  }
  private googleMapTheme(): any[] | undefined {
    if (typeof document === "undefined" || document.documentElement.dataset["theme"] !== "dark") return undefined;
    return [
      { elementType: "geometry", stylers: [{ color: "#303030" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#d1d5db" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#303030" }] },
      { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#ff7b7b" }] },
      { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#ff7b7b" }] },
      { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#303030" }] },
      { featureType: "poi", elementType: "geometry", stylers: [{ color: "#343434" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: "#3b3b3b" }] },
      { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#242424" }] },
      { featureType: "water", elementType: "geometry", stylers: [{ color: "#252525" }] },
    ];
  }
  private normaliseDistrictName(district: string): string {
    const aliases: Record<string, string> = {
      "mumbai city": "mumbai",
      "chhatrapati sambhajinagar": "aurangabad",
    };
    const normalised = district.trim().toLowerCase();
    return aliases[normalised] ?? normalised;
  }
  private showDistrictTooltip(event: any): void {
    const district = event.feature.getProperty("displayDistrict") as string;
    const deviation = event.feature.getProperty("deviation") as number | null;
    const darkTheme = document.documentElement.dataset["theme"] === "dark";
    const surface = darkTheme ? "#1e3654" : "#ffffff";
    const text = darkTheme ? "#e2e8f0" : "#0f172a";
    const muted = darkTheme ? "#cbd5e1" : "#475569";
    const netValue = typeof deviation === "number" ? this.deviationLabel(deviation) : "No data";
    if (!this.districtMapElement) return;
    this.hideDistrictTooltip();
    const tooltip = document.createElement("div");
    tooltip.style.cssText = `position:absolute;z-index:100000;min-width:190px;padding:8px 10px;border:1px solid ${darkTheme ? "#56718f" : "#cbd5e1"};border-radius:6px;color:${text};background:${surface};box-shadow:0 3px 10px rgb(15 23 42 / 28%);font:500 12px/1.35 Arial,sans-serif;pointer-events:none`;
    tooltip.innerHTML = `
        <div style="margin-bottom:5px;color:${muted};font-size:11px">${district} · Block ${this.currentBlockNumber}</div>
        <div><span style="color:${typeof deviation === "number" ? this.deviationColor(deviation) : "#94a3b8"}">●</span> Deviation (%): <b>${netValue}</b></div>
    `;
    const mapHost = this.districtMapElement.nativeElement.querySelector<HTMLElement>(".gm-style")
      ?? this.districtMapElement.nativeElement;
    const mapRect = mapHost.getBoundingClientRect();
    const pointer = event.domEvent as MouseEvent | undefined;
    const left = pointer ? pointer.clientX - mapRect.left + 14 : mapRect.width / 2;
    const top = pointer ? pointer.clientY - mapRect.top + 14 : mapRect.height / 2;
    tooltip.style.left = `${Math.max(8, Math.min(left, mapRect.width - 205))}px`;
    tooltip.style.top = `${Math.max(8, Math.min(top, mapRect.height - 85))}px`;
    mapHost.appendChild(tooltip);
    this.districtTooltip = tooltip;
  }
  private hideDistrictTooltip(): void {
    this.districtTooltip?.remove();
    this.districtTooltip = undefined;
  }
  private deviationColor(deviation: number): string {
    if (deviation >= 3) return "#15803d";
    if (deviation > 0) return "#65a30d";
    if (deviation <= -3) return "#dc2626";
    return "#f59e0b";
  }
  private deviationLabel(deviation: number): string {
    return `${deviation > 0 ? "+" : ""}${deviation.toFixed(1)}%`;
  }
  private loadGoogleMaps(apiKey: string): Promise<void> {
    if ((window as Window & { google?: unknown }).google) return Promise.resolve();
    const existingScript = document.querySelector<HTMLScriptElement>("script[data-google-maps]");
    if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Google Maps script failed to load")), { once: true });
      });
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.dataset["googleMaps"] = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Google Maps script failed to load"));
      document.head.appendChild(script);
    });
  }
  private renderGenerationChart(): void {
    this.generationChart?.destroy();
    if (!this.generationChartElement) return;
    this.generationChart = Highcharts.chart(
      this.generationChartElement.nativeElement,
      {
        chart: {
          type: "column",
          height: 210,
          backgroundColor: "transparent",
          zoomType: "x",
          spacing: [8, 8, 12, 6],
        },
        title: {
          text: "Schedule vs Actual Generation (MW)",
          align: "left",
          style: { color: "#141824", fontSize: "16px", fontWeight: "300" },
        },
        subtitle: {
          text: "(Today)",
          align: "right",
          style: { color: "#64748b", fontSize: "11px" },
        },
        credits: { enabled: false },
        xAxis: {
          categories: ["Solar", "Wind"],
          lineColor: "#d9e2ef",
          labels: { style: { color: "#64748b", fontSize: "10px" } },
        },
        yAxis: {
          min: 0,
          title: { text: "MW", style: { color: "#64748b", fontSize: "10px" } },
          gridLineColor: "#edf1f6",
          labels: { style: { color: "#64748b", fontSize: "10px" } },
        },
        legend: {
          align: "center",
          verticalAlign: "top",
          itemStyle: { color: "#475569", fontSize: "10px", fontWeight: "600" },
          symbolWidth: 12,
        },
        tooltip: { shared: true, valueSuffix: " MW" },
        plotOptions: {
          column: {
            borderWidth: 0,
            borderRadius: 2,
            pointPadding: 0.08,
            groupPadding: 0.16,
          },
          series: {
            dataLabels: {
              enabled: true,
              style: { color: "#334155", fontSize: "9px", textOutline: "none" },
            },
          },
        },
        series: [
          {
            type: "column",
            name: "Scheduled (MW)",
            data: [5120, 3250],
            color: "#cbd5e1",
          },
          {
            type: "column",
            name: "Actual (MW)",
            data: [5340, 3412],
            color: "#2563eb",
          },
        ],
      },
    );
  }
 private renderSourceShareChart(): void {
  this.sourceShareChart?.destroy();

  if (!this.sourceShareChartElement) return;

  const darkTheme =
    document.documentElement.dataset["theme"] === "dark";

  const textColor = darkTheme ? "#f1f5f9" : "#141824";
  const mutedColor = darkTheme ? "#cbd5e1" : "#475569";
  const sourcePanel = this.sourceShareChartElement.nativeElement.closest<HTMLElement>(".chart-panel");
  const isFullscreen = document.fullscreenElement === sourcePanel;
  const chartHeight = isFullscreen ? Math.max(400, (sourcePanel?.clientHeight ?? 0) - 48) : 310;

  this.sourceShareChart = Highcharts.chart(
    this.sourceShareChartElement.nativeElement,
    {
      chart: {
        type: "pie",
        height: chartHeight,
        backgroundColor: "transparent",
        spacing: [5, 5, 5, 5],
      },

      title: {
        text: "Deviation Share by Source (MW)",
        align: "left",
        x: 4,
        y: 5,
        style: {
          color: textColor,
          fontSize: isFullscreen ? "22px" : "14px",
          fontWeight: "500",
        },
      },

      credits: {
        enabled: false,
      },

    tooltip: {
  useHTML: true,

  backgroundColor: darkTheme
    ? "#1e293b"
    : "#ffffff",

  borderColor: darkTheme
    ? "#334155"
    : "#e2e8f0",

  borderRadius: 3,
  shadow: false,

  style: {
    color: textColor,
    fontSize: "13px",
  },

  formatter: function () {
    const percentage = this.percentage ?? 0;

    return `
      <div style="font-size:${isFullscreen ? "17px" : "12px"};">
        <b>${this.name}</b><br>
        Share:
        <b>${Highcharts.numberFormat(percentage, 2)}%</b>
      </div>
    `;
  },
},
      plotOptions: {
        pie: {
          innerSize: "52%",
          size: isFullscreen ? "68%" : "78%",
          center: ["38%", "55%"],
          borderWidth: 0,

          dataLabels: {
            enabled: true,
            color: darkTheme ? "#ffffff" : "#172033",

            // Keep percentage inside donut
            distance: -12,

            crop: false,
            overflow: "allow",

            formatter: function () {
              const percentage = this.percentage ?? 0;

              return `${Highcharts.numberFormat(
                percentage,
                0
              )}%`;
            },

            style: {
              color: darkTheme ? "#f8fafc" : "#172033",
              fontSize: isFullscreen ? "20px" : "13px",
              fontWeight: "700",
              textOutline: darkTheme ? "1px #172033" : "none",
            },
          },

          showInLegend: true,

          point: {
            events: {
              click: function () {
                this.setVisible(!this.visible);
              },
            },
          },
        },
      },

      legend: {
        align: "right",
        verticalAlign: "middle",
        layout: "vertical",

        x: -2,
        y: 5,

        symbolWidth: 10,
        symbolHeight: 10,
        symbolRadius: 5,

        itemMarginTop: 2,
        itemMarginBottom: 2,

        itemStyle: {
          color: mutedColor,
          fontSize: isFullscreen ? "18px" : "12px",
          fontWeight: "600",
        },

        labelFormatter: function () {
          const point = this as Highcharts.Point;
          const percentage = point.percentage ?? 0;

          return `${point.name} ${Highcharts.numberFormat(
            percentage,
            1
          )}%`;
        },
      },

      series: [
        {
          type: "pie",
          name: "Share",

          data: this.sourceShare.map((source) => ({
            name: source.name,
            y: Number.parseFloat(source.value),
            color: source.color,
            dataLabels: {
              color: darkTheme
                ? "#ffffff"
                : source.name === "Solar"
                  ? "#172033"
                  : "#f8fafc",
              style: {
                color: source.name === "Solar" ? "#172033" : "#f8fafc",
              },
            },
          })),
        },
      ],
    }
  );
}
  private deviationChartOptions(
    title: string,
    xAxis: Highcharts.XAxisOptions,
    over: Array<number | null>,
    under: Array<number | null>,
    net: Array<number | null>,
    mode: ChartMode,
  ): Highcharts.Options {
    const type = mode === "time" ? "areaspline" : "column";
    const series: Highcharts.SeriesOptionsType[] = [
      {
        type,
        name: "Over Injection (MW)",
        data: over,
        color: "#16a34a",
        fillColor: "#16a34a2e",
      },
      {
        type,
        name: "Under Injection (MW)",
        data: under,
        color: "#f28b11",
        fillColor: "#f28b112e",
      },
      {
        type,
        name: "Net Deviation (MW)",
        data: net,
        color: "#2563eb",
        fillColor: "#2563eb24",
        lineWidth: 2,
        zIndex: 3,
      },
    ];

    return {
      chart: {
        type,
        height: 250,
        backgroundColor: "transparent",
        zoomType: "x",
        spacing: [10, 8, 6, 6],
      },
      title: {
        text: title,
        align: "left",
        style: { color: "#141824", fontSize: "16px", fontWeight: "300" },
      },
      credits: { enabled: false },
      xAxis,
      yAxis: {
        title: { text: "MW", style: { color: "#64748b", fontSize: "10px" } },
        gridLineColor: "#edf1f6",
        labels: { style: { color: "#64748b", fontSize: "10px" } },
      },
      legend: {
        align: "center",
        verticalAlign: "top",
        itemStyle: { color: "#475569", fontSize: "10px", fontWeight: "600" },
        symbolWidth: 12,
      },
      tooltip: { shared: true },
      plotOptions: {
        series: { marker: { enabled: false } },
        areaspline: {
          lineWidth: 1.5,
          fillOpacity: 0.18,
        },
        column: { grouping: false, pointPadding: 0.04, groupPadding: 0.08 },
      },
      series,
    };
  }
}
